import { FormikProps } from "formik"
import { isNil } from "lodash"
import { enqueueSnackbar } from "notistack"
import { useRef } from "react"
import { useNavigate } from "react-router-dom"

import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { COMPANY_ADMIN_ACCOUNT_ADMINS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { selectEffectiveCompanyId } from "../../../../../context/contextSelectors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { UserType } from "../../../../../login/loginTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { AdministratorForm } from "../../../../../users/administrators/components/AdministratorForm"
import { AdministratorFormValues } from "../../../../../users/administrators/administratorFormTypes"
import { toCreateAdministratorFields } from "../../../../../users/administrators/utils/administratorBody"
import { createAdmin } from "../../../../../users/userApi"
import { CreateCompanyAdmin } from "../../../../../users/userTypes"
import { CompanyAdminsQuery } from "../adminsApi"

export function CreateCompanyAdminPage() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const companyId = useAppSelector(selectEffectiveCompanyId)

    const formRef = useRef<FormikProps<AdministratorFormValues>>(null)

    const { mutateAsync: callCreateAdmin, isLoading: isCreating } = useApiMutation(createAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminsQuery.ADMINS_FOR_COMPANY] })

            navigate(COMPANY_ADMIN_ACCOUNT_ADMINS_ROUTE)
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    async function handleSubmit(values: AdministratorFormValues) {
        if (isNil(companyId)) {
            enqueueSnackbar(l10n.getString("office-manager-create-admin-missing-context-error"), { variant: "error" })
            return
        }

        const admin: CreateCompanyAdmin = {
            type: UserType.COMPANY,
            ...toCreateAdministratorFields(values),
            companyDetails: { companyId },
        }

        await callCreateAdmin({ admin })
    }

    return (
        <>
            <LocalizedStrict id="administrators-create-page-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_CREATE_ADMIN}
                    title="Create administrator"
                    rightElement={
                        <LocalizedStrict id="administrators-create-page-save-button">
                            <StandardLoadingButton type="submit" onClick={handleSaveClick} loading={isCreating}>
                                Create
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-admins">
                            <BreadcrumbLink to={COMPANY_ADMIN_ACCOUNT_ADMINS_ROUTE}>Administrators</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="administrators-create-breadcrumb">
                            <Breadcrumb>Create administrator</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <AdministratorForm ref={formRef} adminType={UserType.COMPANY} isEditMode={false} onSubmit={handleSubmit} />
        </>
    )
}
